import React, { useRef, useEffect, useState } from "react";
import moment from "moment";
import { Button, Card, Typography, makeStyles } from "@material-ui/core";
import PrintIcon from "@material-ui/icons/Print";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(1.25),
    maxWidth: "800px",
    margin: "0 auto",
  },
  headerBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(0.75),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleText: {
    fontSize: "1rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  printButton: {
    textTransform: "none",
    padding: theme.spacing(0.5, 1.25),
    fontSize: "0.8125rem",
  },
  reportWrapper: {
    width: "100%",
    fontFamily: "Arial, sans-serif",
    "& @media print": {
      width: "80mm",
      margin: "0 5mm",
    },
  },
  headerText: {
    fontSize: "0.9375rem",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: theme.spacing(0.5),
  },
  divider: {
    borderBottom: "2px dotted #000",
    margin: "8px 0",
  },
  dataText: {
    fontSize: "0.8125rem",
    lineHeight: 1.4,
    "& > div": {
      marginBottom: "2px",
    },
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: theme.spacing(1),
    "& th": {
      fontSize: "0.8125rem",
      padding: "4px 0",
      borderBottom: "1px solid #ddd",
    },
    "& td": {
      fontSize: "0.75rem",
      padding: "4px 0",
    },
  },
  summaryText: {
    fontSize: "0.8125rem",
    "& > div": {
      margin: "4px 0",
    },
  },
  flexBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionTitle: {
    fontSize: "0.875rem",
    fontWeight: "bold",
    margin: "10px 0 4px 0",
  },
  footer: {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "0.75rem",
    marginTop: theme.spacing(1),
  },
}));

const ThermalSalesReport = ({
  userProfile,
  salesData,
  selectedDate,
  reportType: reportTypeProp,
  selectedEmployeeName,
}) => {
  const reportType = reportTypeProp || "consolidated";
  const classes = useStyles();
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const printRef = useRef();

  useEffect(() => {
    const filtered = (salesData.invoices || []).filter(
      (invoice) =>
        moment(invoice.createdAt).format("YYYY-MM-DD") ===
        moment(selectedDate).format("YYYY-MM-DD")
    );
    setFilteredInvoices(filtered);
  }, [selectedDate, salesData.invoices]);

  //   const handleDateChange = (date) => {
  //     setSelectedDate(date);
  //   };

  const handlePrint = () => {
    const printContent = document.getElementById("thermal-sales-report");

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.write(
      "<!DOCTYPE html><html><head><title>Sales Report</title>" +
        "<style>" +
        "@page { size: 80mm auto; margin: 0mm 5mm 0mm 5mm; }" +
        "@media print {" +
        "  body { margin: 0; padding: 0; width: 80mm; }" +
        '  .report-wrapper { width: 72mm; padding: 2mm 4mm; font-family: "Arial", sans-serif; }' +
        "  .text-center { text-align: center; }" +
        "  .text-right { text-align: right; }" +
        "  .bold { font-weight: bold; }" +
        "  .divider { border-bottom: 1px dotted #000; margin: 5px 0; }" +
        "  table { width: 100%; border-collapse: collapse; }" +
        "  th, td { padding: 2px 0; }" +
        "  .header-text { font-size: 14px; }" +
        "  .data-text { font-size: 12px; }" +
        "  .summary-text { font-size: 13px; }" +
        "}" +
        "</style></head><body>" +
        printContent.innerHTML +
        "</body></html>"
    );
    iframeDoc.close();

    iframe.contentWindow.onafterprint = () => {
      document.body.removeChild(iframe);
    };

    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  };

  // Calculate category-wise totals
  // const categoryTotals = filteredInvoices.reduce((acc, invoice) => {
  //   invoice.items.forEach((item) => {
  //     const category = item?.item?.category?.categoryName || "Uncategorized";
  //     acc[category] =
  //       (acc[category] || 0) + item.quantity * item?.item?.units?.sale?.price;
  //   });
  //   return acc;
  // }, {});

  const getItemRate = (item) => {
    const raw =
      item?.rate ??
      item?.overridePrice ??
      item?.item?.units?.sale?.price ??
      0;
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getItemQty = (item) => {
    const raw = item?.quantity ?? item?.qty ?? 0;
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const computeTotals = (invoices) => {
    const catTotals = (invoices || []).reduce((acc, invoice) => {
      (invoice.items || []).forEach((item) => {
        const category = item?.item?.category?.categoryName || "Uncategorized";
        if (category !== "Uncategorized") {
          const lineAmount = getItemQty(item) * getItemRate(item);
          acc[category] = (acc[category] || 0) + lineAmount;
        }
      });
      return acc;
    }, {});
    const payTotals = (invoices || []).reduce((acc, invoice) => {
      const hasUncategorized = (invoice.items || []).some(
        (item) => !item?.item?.category?.categoryName
      );
      if (!hasUncategorized) {
        const mode = invoice.paymentMode || "Other";
        acc[mode] = (acc[mode] || 0) + (invoice.total || 0);
      }
      return acc;
    }, {});
    const totalSalesVal = Object.values(catTotals).reduce((a, b) => a + b, 0);
    const totalDiscountVal = (invoices || []).reduce((acc, invoice) => {
      const hasUncategorized = (invoice.items || []).some(
        (item) => !item?.item?.category?.categoryName
      );
      return hasUncategorized ? acc : acc + (invoice.discount || 0);
    }, 0);
    const totalVatVal = (invoices || []).reduce((acc, invoice) => {
      const hasUncategorized = (invoice.items || []).some(
        (item) => !item?.item?.category?.categoryName
      );
      return hasUncategorized ? acc : acc + (invoice.vat || 0);
    }, 0);
    return {
      categoryTotals: catTotals,
      paymentTotals: payTotals,
      totalSales: totalSalesVal,
      totalDiscount: totalDiscountVal,
      totalVat: totalVatVal,
    };
  };

  const byEmployee =
    reportType === "employee-wise"
      ? filteredInvoices.reduce((acc, inv) => {
          const key = inv.employeeName || inv.employeeId || "Unassigned";
          if (!acc[key]) acc[key] = [];
          acc[key].push(inv);
          return acc;
        }, {})
      : null;

  const totals = computeTotals(filteredInvoices);
  const {
    categoryTotals,
    paymentTotals,
    totalSales,
    totalDiscount,
    totalVat,
  } = totals;

  const employeeSummary =
    reportType === "employee-wise" && byEmployee
      ? Object.entries(byEmployee).map(([empName, invs]) => {
          const t = computeTotals(invs);
          return {
            employeeName: empName,
            orderCount: invs.length,
            totalCollection: invs.reduce((s, i) => s + (i.total || 0), 0),
            ...t,
          };
        })
      : [];

  const reportTitle =
    reportType === "employee-wise"
      ? selectedEmployeeName
        ? `Daily Sales Report – ${selectedEmployeeName}`
        : "Daily Sales Report (Employee-wise)"
      : "Daily Sales Report";

  return (
    <Card className={classes.card}>
      <div className={classes.headerBar}>
        <Typography className={classes.titleText}>{reportTitle}</Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<PrintIcon fontSize="small" />}
          onClick={handlePrint}
          className={classes.printButton}
        >
          Print Report
        </Button>
      </div>

      <div style={{ display: "none" }}>
        <div
          id="thermal-sales-report"
          className="report-wrapper"
          ref={printRef}
        >
          <div className="text-center bold header-text">
            {reportTitle}
          </div>
          <div className="text-center bold header-text">
            {userProfile.businessName}
          </div>

          <div className="divider" />

          <div className="data-text">
            <div>Date: {moment(selectedDate).format("DD/MM/YYYY")}</div>
            <div>Printed On: {moment().format("DD/MM/YYYY HH:mm:ss")}</div>
            <div>Total Orders: {filteredInvoices.length}</div>
          </div>

          {reportType === "employee-wise" && employeeSummary.length > 0 && (
            <>
              <div className="divider" />
              <div className="bold header-text">Employee-wise collection</div>
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }} className="header-text">Employee</th>
                    <th style={{ textAlign: "right" }} className="header-text">Orders</th>
                    <th style={{ textAlign: "right" }} className="header-text">Collection (฿)</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeSummary.map((row) => (
                    <tr key={row.employeeName} className="data-text">
                      <td style={{ textAlign: "left" }}>{row.employeeName}</td>
                      <td style={{ textAlign: "right" }}>{row.orderCount}</td>
                      <td style={{ textAlign: "right" }}>{row.totalCollection.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div className="divider" />

          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }} className="header-text">
                  Category
                </th>
                <th style={{ textAlign: "right" }} className="header-text">
                  Amount(฿)
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(categoryTotals).map(([category, amount]) => (
                <tr key={category} className="data-text">
                  <td style={{ textAlign: "left" }}>{category}</td>
                  <td style={{ textAlign: "right" }}>{amount.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="divider" />

          <div className="summary-text">
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Total Sales</span>
              <span>฿{totalSales.toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Total Discount</span>
              <span>฿{totalDiscount.toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Total VAT</span>
              <span>฿{totalVat.toFixed(2)}</span>
            </div>
          </div>

          <div className="divider" />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Net Sales</span>
            <span>฿{(totalSales - totalDiscount + totalVat).toFixed(2)}</span>
          </div>
          <div className="bold header-text">Payment Mode Summary</div>
          {Object.entries(paymentTotals).map(([mode, amount]) => (
            <div
              key={mode}
              className="summary-text"
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <span>{mode}</span>
              <span>฿{amount.toFixed(2)}</span>
            </div>
          ))}

          <div className="divider" />

          <div className="text-center bold">*** End of Report ***</div>
        </div>
      </div>

      <div
        id="thermal-sales-report1"
        className={classes.reportWrapper}
        ref={printRef}
      >
        <div className={classes.headerText}>
          {reportTitle}
        </div>
        <div className={classes.headerText}>{userProfile.businessName}</div>

        <div className={classes.divider} />

        <div className={classes.dataText}>
          <div>Date: {moment(selectedDate).format("DD/MM/YYYY")}</div>
          <div>Printed On: {moment().format("DD/MM/YYYY HH:mm:ss")}</div>
          <div>Total Orders: {filteredInvoices.length}</div>
        </div>

        {reportType === "employee-wise" && employeeSummary.length > 0 && (
          <>
            <div className={classes.divider} />
            <div className={classes.sectionTitle}>Employee-wise collection</div>
            <table className={classes.table} style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Employee</th>
                  <th style={{ textAlign: "right" }}>Orders</th>
                  <th style={{ textAlign: "right" }}>Collection (฿)</th>
                </tr>
              </thead>
              <tbody>
                {employeeSummary.map((row) => (
                  <tr key={row.employeeName}>
                    <td style={{ textAlign: "left" }}>{row.employeeName}</td>
                    <td style={{ textAlign: "right" }}>{row.orderCount}</td>
                    <td style={{ textAlign: "right" }}>{row.totalCollection.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className={classes.divider} />

        <table className={classes.table}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Category</th>
              <th style={{ textAlign: "right" }}>Amount(฿)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(categoryTotals).map(([category, amount]) => (
              <tr key={category}>
                <td style={{ textAlign: "left" }}>{category}</td>
                <td style={{ textAlign: "right" }}>{amount.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={classes.divider} />

        <div className={classes.summaryText}>
          <div className={classes.flexBetween}>
            <span>Total Sales</span>
            <span>฿{totalSales.toFixed(2)}</span>
          </div>
          <div className={classes.flexBetween}>
            <span>Total Discount</span>
            <span>฿{totalDiscount.toFixed(2)}</span>
          </div>
          <div className={classes.flexBetween}>
            <span>Total VAT</span>
            <span>฿{totalVat.toFixed(2)}</span>
          </div>
        </div>

        <div className={classes.divider} />

        <div className={classes.flexBetween}>
          <span className={classes.sectionTitle}>Net Sales</span>
          <span className={classes.sectionTitle}>
            ฿{(totalSales - totalDiscount + totalVat).toFixed(2)}
          </span>
        </div>

        <div className={classes.sectionTitle}>Payment Mode Summary</div>
        {Object.entries(paymentTotals).map(([mode, amount]) => (
          <div key={mode} className={classes.flexBetween}>
            <span>{mode}</span>
            <span>฿{amount.toFixed(2)}</span>
          </div>
        ))}

        <div className={classes.divider} />

        <div className={classes.footer}>*** End of Report ***</div>
      </div>
    </Card>
  );
};

export default ThermalSalesReport;
